/**
 * Utility functions for consolidating similar rewards into grouped entries
 * Instead of showing 500 individual $0.1 rewards, show "Common Treasure (500x = $50)"
 */

import type { Reward } from '@/types/schemas';

export interface ConsolidatedReward {
  type: Reward['type'];
  tier?: number;
  tier_name: string;
  tier_color?: string;
  count: number;
  individual_amount_lamports: string;
  individual_usd_value?: number;
  total_amount_lamports: string;
  total_usd_value?: number;
  is_jackpot_win: boolean;
  original_rewards: Reward[];
}

/**
 * Groups rewards by their type, tier, and amount to consolidate similar rewards
 */
export function consolidateRewards(rewards: Reward[]): ConsolidatedReward[] {
  if (rewards.length === 0) return [];

  // Group rewards by their key characteristics
  const groupedRewards = new Map<string, Reward[]>();

  for (const reward of rewards) {
    // Create a unique key for grouping similar rewards
    const key = `${reward.type}_${reward.tier}_${reward.tier_name}_${reward.amount_lamports}_${reward.usd_value || 0}`;

    if (!groupedRewards.has(key)) {
      groupedRewards.set(key, []);
    }
    groupedRewards.get(key)!.push(reward);
  }

  // Convert groups to consolidated rewards
  const consolidated: ConsolidatedReward[] = [];

  for (const [, groupRewards] of Array.from(groupedRewards.entries())) {
    const firstReward = groupRewards[0];
    const count = groupRewards.length;

    // Calculate totals
    const totalAmountLamports = groupRewards.reduce(
      (sum: bigint, reward: Reward) => sum + BigInt(reward.amount_lamports),
      BigInt(0)
    );

    const totalUsdValue = groupRewards.reduce(
      (sum: number, reward: Reward) => sum + (reward.usd_value || 0),
      0
    );

    consolidated.push({
      type: firstReward.type,
      tier: firstReward.tier,
      tier_name: firstReward.tier_name || 'Unknown',
      tier_color: firstReward.tier_color,
      count,
      individual_amount_lamports: firstReward.amount_lamports,
      individual_usd_value: firstReward.usd_value,
      total_amount_lamports: totalAmountLamports.toString(),
      total_usd_value: totalUsdValue > 0 ? totalUsdValue : undefined,
      is_jackpot_win: firstReward.is_jackpot_win || false,
      original_rewards: groupRewards,
    });
  }

  // Sort by total value descending (most valuable first)
  consolidated.sort((a, b) => {
    const aValue = Number(a.total_amount_lamports);
    const bValue = Number(b.total_amount_lamports);
    return bValue - aValue;
  });

  return consolidated;
}

/**
 * Format a consolidated reward for display
 */
export function formatConsolidatedReward(reward: ConsolidatedReward): {
  displayName: string;
  displayValue: string;
  displayCount: string;
} {
  const displayName = reward.tier_name;

  let displayValue: string;
  if (reward.total_usd_value && reward.total_usd_value > 0) {
    if (reward.count === 1) {
      displayValue = `$${reward.total_usd_value.toFixed(2)}`;
    } else {
      const individualUsd = reward.individual_usd_value || 0;
      displayValue = `$${individualUsd.toFixed(2)} × ${reward.count} = $${reward.total_usd_value.toFixed(2)}`;
    }
  } else {
    const totalSol = Number(reward.total_amount_lamports) / 1e9;
    if (reward.count === 1) {
      displayValue = `${totalSol.toFixed(4)} SOL`;
    } else {
      const individualSol = Number(reward.individual_amount_lamports) / 1e9;
      displayValue = `${individualSol.toFixed(4)} SOL × ${reward.count} = ${totalSol.toFixed(4)} SOL`;
    }
  }

  const displayCount = reward.count > 1 ? `${reward.count}×` : '';

  return { displayName, displayValue, displayCount };
}

/**
 * Get appropriate emoji for reward type and tier
 */
export function getConsolidatedRewardEmoji(reward: ConsolidatedReward): string {
  if (reward.is_jackpot_win) {
    return '👑'; // Jackpot
  }

  switch (reward.tier_color) {
    case 'golden':
      return '🏆'; // Legendary
    case 'purple':
      return '💎'; // Epic
    case 'blue':
      return '💠'; // Rare
    case 'white':
    default:
      return '📦'; // Common
  }
}

/**
 * Calculate summary statistics for consolidated rewards
 */
export function getRewardSummary(consolidated: ConsolidatedReward[]): {
  totalRewards: number;
  totalValue: number;
  jackpotCount: number;
  uniqueTypes: number;
} {
  const totalRewards = consolidated.reduce((sum, reward) => sum + reward.count, 0);
  const totalValue = consolidated.reduce((sum, reward) => {
    return sum + (reward.total_usd_value || (Number(reward.total_amount_lamports) / 1e9) * 200);
  }, 0);
  const jackpotCount = consolidated.filter((reward) => reward.is_jackpot_win).length;
  const uniqueTypes = consolidated.length;

  return { totalRewards, totalValue, jackpotCount, uniqueTypes };
}
